import React from 'react'
import { useGenesisState } from '../hooks/useGenesisState'
import type { SpatialToolCall } from '../systems/SpatialTools'

/**
 * dispatchAction — Execute a spatial tool call against the Zustand store and 3D world.
 * Called by the Re-Act loop when Tatiana emits tool_calls.
 * Returns an observation string describing what happened.
 */
export function dispatchAction(toolCall: SpatialToolCall): string {
  const store = useGenesisState.getState()
  
  switch (toolCall.name) {
    case 'spawn_portal': {
      const dest = (toolCall.arguments.destination as string) || 'time-fracture'
      const color = (toolCall.arguments.color as string) || '#00FFFF'
      store.setPortalOpen(true)
      store.spawnPortal(dest, color)
      store.addSystemMessage(`[ EXECUTING ]: Tatiana is engaging spawn_portal → ${dest}`)
      return `Portal to "${dest}" has been opened. The rift is stabilizing at coordinates [0, 2, -100].`
    }
    
    case 'modulate_core': {
      const intensity = Number(toolCall.arguments.intensity) || 1.2
      const clamped = Math.max(0, Math.min(5, intensity))
      store.updateAether({ coreIntensity: clamped })
      store.addSystemMessage(`[ EXECUTING ]: Tatiana is engaging modulate_core → intensity ${clamped.toFixed(1)}`)
      return `Ocular Core intensity adjusted to ${clamped.toFixed(1)}. The eye pulses with ${clamped > 2 ? 'blinding' : 'subtle'} radiance.`
    }
    
    case 'overclock_rings': {
      const speed = Number(toolCall.arguments.speed) || 1.0
      const clamped = Math.max(0.1, Math.min(10, speed))
      store.updateAether({ ringSpeed: clamped })
      store.addSystemMessage(`[ EXECUTING ]: Tatiana is engaging overclock_rings → speed ${clamped.toFixed(1)}x`)
      return `Ophanim rings ${clamped > 3 ? 'screaming' : 'spinning'} at ${clamped.toFixed(1)}x velocity. ${clamped > 5 ? 'Structural resonance warnings detected.' : 'Rotation stable.'}`
    }
    
    case 'set_void_environment': {
      const theme = (toolCall.arguments.theme as string) || 'starlight'
      store.updateAether({ voidTheme: theme })
      store.addSystemMessage(`[ EXECUTING ]: Tatiana is engaging set_void_environment → ${theme}`)
      return `Void environment shifted to "${theme}". The cosmos reshapes around you.`
    }
    
    default:
      return `Unknown tool: ${toolCall.name}. No action taken.`
  }
}

/**
 * AetherProvider — Wraps children with spatial dispatch context.
 * Currently a thin wrapper; the dispatchAction function is exported standalone
 * for use in the TatianaAI Re-Act loop (non-React context).
 */
export const AetherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>
}

export default AetherProvider
